/**
 * Cookie Consent Manager
 *
 * OPTIMIZED FLOW:
 * 1. Server (globals.json) provides ALL tracking decisions + sets defaults on Party:
 *    - visitor_country, visitor_region, consent_mode, has_gpc
 *    - consent: { analytics, marketing, updated_at } - from Party record
 * 2. This manager initializes when 'trackingConfigReady' event fires
 * 3. For opt-out/implied regions: enable tracking immediately, no banner, no extra roundtrip
 * 4. For opt-in regions: show banner, wait for user action
 * 5. Only POST /globals/consent when user CHANGES preferences via banner
 *
 * @see https://cookieconsent.orestbida.com/
 * @see https://developers.google.com/tag-platform/security/guides/consent
 */

import * as CookieConsent from 'vanilla-cookieconsent'
// Debug utility removed - was causing webpack resolution issues with node_modules/debug package

// Consent categories
const CAT_NECESSARY = 'necessary'
const CAT_ANALYTICS = 'analytics'
const CAT_MARKETING = 'marketing'

// Consent modes (from server)
const CONSENT_MODE = {
  OPT_IN: 'opt_in',      // Must ask before tracking (EU, Quebec)
  OPT_OUT: 'opt_out',    // Can track, must allow opt-out (US)
  IMPLIED: 'implied'     // Can track with notice (Rest of Canada)
}

class ConsentManager {
  static _initialized = false
  static _cookieConsentInitialized = false  // Track if CookieConsent.run() has been called
  static _debug = false
  static _config = null  // Config from server

  /**
   * Initialize when tracking config is ready from server
   * Listens for 'trackingConfigReady' event from primeval.js
   */
  static init() {
    if (this._initialized) return

    // If config already available (globals.json already returned), init immediately
    if (window.trackingConfig) {
      this._initWithConfig(window.trackingConfig)
      return
    }

    // Otherwise wait for the event
    window.addEventListener('trackingConfigReady', (event) => {
      this._initWithConfig(event.detail)
    }, { once: true })
  }

  /**
   * Initialize with config from server
   * Config contains consent already stored on Party - no extra roundtrip needed
   */
  static _initWithConfig(config) {
    if (this._initialized) return
    this._initialized = true
    this._config = config

    const isOptIn = config.consent_mode === CONSENT_MODE.OPT_IN
    // Consent is ALWAYS present now (set on Party during first globals.json)
    const hasAnalyticsConsent = config.consent?.analytics === true
    const hasMarketingConsent = config.consent?.marketing === true
    // User moved from permissive region (US) to strict region (GDPR/Quebec)?
    const requiresReconsent = config.requires_reconsent === true

    if (this._debug) {
      console.log('[ConsentManager] Initializing with server-stored consent:', {
        consentMode: config.consent_mode,
        isOptIn,
        consent: config.consent,
        hasGPC: config.has_gpc,
        requiresReconsent
      })
    }

    // DECISION: Do we need to show the consent UI?
    if (requiresReconsent) {
      // User moved from permissive region to strict region - must re-consent!
      // e.g., US visitor now in Germany - their US "implied consent" doesn't count
      if (this._debug) {
        console.log('[ConsentManager] Re-consent required - user moved to stricter region')
      }
      this._initCookieConsentUI()
    } else if (isOptIn && !hasAnalyticsConsent && !hasMarketingConsent) {
      // OPT-IN region (Quebec, GDPR) with no consent yet → SHOW BANNER
      if (this._debug) {
        console.log('[ConsentManager] Opt-in region, no consent - showing banner')
      }
      this._initCookieConsentUI()
    } else if (isOptIn) {
      // OPT-IN region with existing consent → Initialize UI silently (for footer link)
      if (this._debug) {
        console.log('[ConsentManager] Opt-in region, has consent - silent init')
      }
      this._initCookieConsentUI()
      this._updateGtagConsentFromConfig()
      this._loadTrackingScripts()
    } else {
      // OPT-OUT/IMPLIED region → Just enable tracking, NO banner needed
      // Consent defaults were already set on Party server-side
      if (this._debug) {
        console.log('[ConsentManager] Opt-out/implied region - enabling tracking (no roundtrip)')
      }
      this._enableTrackingWithoutUI()
    }
  }

  /**
   * Enable tracking without any CookieConsent UI
   * Used for opt-out (US) and implied (non-Quebec Canada) regions
   * Consent defaults were already set on Party during globals.json - NO extra roundtrip
   */
  static _enableTrackingWithoutUI() {
    const consent = this._config?.consent || {}
    const analyticsGranted = consent.analytics !== false  // Default true for opt-out
    const marketingGranted = consent.marketing !== false && !this._config?.has_gpc

    // Update Google Consent Mode from server-stored consent
    window.gtag('consent', 'update', {
      'analytics_storage': analyticsGranted ? 'granted' : 'denied',
      'ad_storage': marketingGranted ? 'granted' : 'denied',
      'ad_user_data': marketingGranted ? 'granted' : 'denied',
      'ad_personalization': marketingGranted ? 'granted' : 'denied',
      'personalization_storage': analyticsGranted ? 'granted' : 'denied'
    })

    window.tracVis = analyticsGranted || marketingGranted

    // Load tracking scripts
    this._loadTrackingScripts()

    // NO _syncConsentToServer() needed - defaults already stored on Party!

    // Set client cookie to match server state (for CookieConsent library if user opens preferences later)
    const expires = new Date()
    expires.setFullYear(expires.getFullYear() + 1)
    const categories = [CAT_NECESSARY]
    if (analyticsGranted) categories.push(CAT_ANALYTICS)
    if (marketingGranted) categories.push(CAT_MARKETING)

    document.cookie = `cc_cookie=${encodeURIComponent(JSON.stringify({
      categories,
      revision: 0,
      consentTimestamp: consent.updated_at || new Date().toISOString()
    }))}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`

    if (this._debug) {
      console.log('[ConsentManager] Tracking enabled (opt-out/implied, no roundtrip):', {
        analytics: analyticsGranted,
        marketing: marketingGranted
      })
    }
  }

  /**
   * Update gtag consent from server config (without CookieConsent library)
   */
  static _updateGtagConsentFromConfig() {
    const consent = this._config?.consent || {}
    const analyticsGranted = consent.analytics === true
    const marketingGranted = consent.marketing === true && !this._config?.has_gpc

    window.gtag('consent', 'update', {
      'analytics_storage': analyticsGranted ? 'granted' : 'denied',
      'ad_storage': marketingGranted ? 'granted' : 'denied',
      'ad_user_data': marketingGranted ? 'granted' : 'denied',
      'ad_personalization': marketingGranted ? 'granted' : 'denied',
      'personalization_storage': analyticsGranted ? 'granted' : 'denied'
    })

    window.tracVis = analyticsGranted || marketingGranted
  }

  /**
   * Initialize the CookieConsent UI
   */
  static _initCookieConsentUI() {
    if (this._cookieConsentInitialized) return
    this._cookieConsentInitialized = true

    const config = this._config
    const isOptOut = config.consent_mode === CONSENT_MODE.OPT_OUT
    const isImplied = config.consent_mode === CONSENT_MODE.IMPLIED

    // Check if cookie already exists (from _enableTrackingWithoutUI or previous visit)
    const existingCookie = this._getCookieValue('cc_cookie')
    const hasExistingConsent = existingCookie !== null

    // For opt-out/implied regions with existing cookie, disable autoShow to prevent banner
    // According to config reference: https://cookieconsent.orestbida.com/reference/configuration-reference.html
    // autoShow: Automatically show the consent modal if consent is not valid (default: true)
    const shouldAutoShow = !(hasExistingConsent && (isOptOut || isImplied))

    CookieConsent.run({
      cookie: {
        expiresAfterDays: 365
      },

      // Disable autoShow if cookie exists in opt-out/implied regions (prevents double popup)
      autoShow: shouldAutoShow,

      // For opt-in regions, block page until consent given
      disablePageInteraction: config.consent_mode === CONSENT_MODE.OPT_IN,

      guiOptions: {
        consentModal: {
          layout: 'box inline',
          position: 'bottom left',
          equalWeightButtons: true,
          flipButtons: true
        },
        preferencesModal: {
          layout: 'box',
          position: 'right',
          equalWeightButtons: true,
          flipButtons: false
        }
      },

      categories: {
        [CAT_NECESSARY]: {
          enabled: true,
          readOnly: true
        },
        [CAT_ANALYTICS]: {
          enabled: isOptOut || isImplied,
          readOnly: false,
          autoClear: {
            cookies: [
              { name: /^_ga/ },
              { name: '_gid' },
              { name: /^_gat/ },
              { name: '_gcl_au' }
            ]
          }
        },
        [CAT_MARKETING]: {
          enabled: (isOptOut || isImplied) && !config.has_gpc,
          readOnly: false,
          autoClear: {
            cookies: [
              { name: '_fbp' },
              { name: '_fbc' },
              { name: /^_uet/ }
            ]
          }
        }
      },

      language: {
        default: 'en',
        translations: {
          en: this._getTranslations()
        }
      },

      onFirstConsent: () => {
        this._updateGtagConsent()
        this._syncConsentToServer()
        this._loadTrackingScripts()
      },

      onConsent: () => {
        this._updateGtagConsent()
      },

      onChange: ({ changedCategories }) => {
        this._updateGtagConsent()
        this._syncConsentToServer()

        if (changedCategories.includes(CAT_MARKETING)) {
          window.location.reload()
        }
      }
    })
  }

  /**
   * Get UI translations
   */
  static _getTranslations() {
    return {
      consentModal: {
        title: 'We use cookies',
        description: 'We use cookies to analyze site traffic and improve your experience. You can choose to accept all cookies or manage your preferences.',
        acceptAllBtn: 'Accept all',
        acceptNecessaryBtn: 'Reject all',
        showPreferencesBtn: 'Manage preferences',
        footer: '<a href="/company/privacy-policy">Privacy Policy</a>\n<a href="/company/terms-of-use">Terms of Use</a>'
      },
      preferencesModal: {
        title: 'Manage Cookie Preferences',
        acceptAllBtn: 'Accept All',
        acceptNecessaryBtn: 'Essential Only',
        savePreferencesBtn: 'Save Preferences',
        closeIconLabel: 'Close',
        sections: [
          {
            title: 'Cookie Usage',
            description: 'We use cookies to ensure the basic functionalities of the website and to enhance your online experience.'
          },
          {
            title: 'Strictly Necessary Cookies',
            description: 'These cookies are essential for the website to function properly.',
            linkedCategory: CAT_NECESSARY
          },
          {
            title: 'Analytics Cookies',
            description: 'These cookies help us understand how visitors interact with our website.',
            linkedCategory: CAT_ANALYTICS,
            cookieTable: {
              headers: { name: 'Name', domain: 'Service', description: 'Description', expiration: 'Expiration' },
              body: [
                { name: '_ga', domain: 'Google Analytics', description: 'Used to distinguish users', expiration: '2 years' },
                { name: '_gid', domain: 'Google Analytics', description: 'Used to distinguish users', expiration: '24 hours' }
              ]
            }
          },
          {
            title: 'Marketing Cookies',
            description: 'These cookies help us show you relevant advertisements.',
            linkedCategory: CAT_MARKETING,
            cookieTable: {
              headers: { name: 'Name', domain: 'Service', description: 'Description', expiration: 'Expiration' },
              body: [
                { name: '_fbp', domain: 'Facebook', description: 'Advertising tracking', expiration: '3 months' },
                { name: '_uet*', domain: 'Microsoft/Bing', description: 'Advertising tracking', expiration: '13 months' }
              ]
            }
          },
          {
            title: 'More Information',
            description: 'For questions about our cookie practices, please <a href="/contact" class="cc-link">contact us</a>.'
          }
        ]
      }
    }
  }

  /**
   * Update Google Consent Mode v2 (after user interacts with CookieConsent UI)
   */
  static _updateGtagConsent() {
    const analyticsAccepted = CookieConsent.acceptedCategory(CAT_ANALYTICS)
    const marketingAccepted = CookieConsent.acceptedCategory(CAT_MARKETING)
    const hasGPC = this._config?.has_gpc || navigator.globalPrivacyControl === true
    const effectiveMarketing = hasGPC ? false : marketingAccepted

    window.gtag('consent', 'update', {
      'analytics_storage': analyticsAccepted ? 'granted' : 'denied',
      'ad_storage': effectiveMarketing ? 'granted' : 'denied',
      'ad_user_data': effectiveMarketing ? 'granted' : 'denied',
      'ad_personalization': effectiveMarketing ? 'granted' : 'denied',
      'personalization_storage': analyticsAccepted ? 'granted' : 'denied'
    })

    // Update window.tracVis for Analytics service
    window.tracVis = analyticsAccepted || effectiveMarketing

    if (this._debug) {
      console.log('[ConsentManager] Updated consent:', {
        analytics: analyticsAccepted,
        marketing: effectiveMarketing,
        hasGPC,
        tracVis: window.tracVis
      })
    }

    window.dispatchEvent(new CustomEvent('consentUpdated', {
      detail: { analytics: analyticsAccepted, marketing: effectiveMarketing, hasGPC }
    }))
  }

  /**
   * Sync consent to server when user CHANGES preferences
   * Only called when user interacts with CookieConsent UI (not for defaults)
   * Updates Party's consent_preferences for persistence
   */
  static _syncConsentToServer() {
    const analyticsAccepted = CookieConsent.acceptedCategory(CAT_ANALYTICS)
    const marketingAccepted = CookieConsent.acceptedCategory(CAT_MARKETING)
    const hasGPC = this._config?.has_gpc || navigator.globalPrivacyControl === true

    const payload = {
      consent_mode: this._config?.consent_mode,
      consent_country: this._config?.visitor_country,
      consent_region: this._config?.visitor_region,
      analytics_consent: analyticsAccepted,
      marketing_consent: marketingAccepted && !hasGPC,
      has_gpc: hasGPC
    }

    if (this._debug) {
      console.log('[ConsentManager] User changed consent - syncing to server:', payload)
    }

    fetch('/globals/consent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      },
      body: JSON.stringify(payload)
    }).catch(error => {
      console.error('[ConsentManager] Failed to sync consent:', error)
    })
  }

  /**
   * Load tracking scripts (GA4, Facebook, Bing, Clarity)
   */
  static _loadTrackingScripts() {
    // Check consent from CookieConsent library if initialized, else from server config
    const analyticsAccepted = this._cookieConsentInitialized
      ? CookieConsent.acceptedCategory?.(CAT_ANALYTICS) !== false
      : (this._config?.consent?.analytics !== false)
    const marketingAccepted = (this._cookieConsentInitialized
      ? CookieConsent.acceptedCategory?.(CAT_MARKETING) !== false
      : (this._config?.consent?.marketing !== false)) &&
      !(this._config?.has_gpc || navigator.globalPrivacyControl === true)

    // debug('consent', '_loadTrackingScripts', { analyticsAccepted, marketingAccepted, tracVis: window.tracVis })

    if (analyticsAccepted || marketingAccepted) {
      // Load GA4 if not already loaded
      if (!document.querySelector('script[src*="googletagmanager.com/gtag"]')) {
        // debug('consent', 'Loading GA4 (analytics accepted)')
        const gaScript = document.createElement('script')
        gaScript.async = true
        gaScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-0W79EHXE56'
        document.head.appendChild(gaScript)

        window.gtag('js', new Date())
        window.gtag('config', 'G-0W79EHXE56')
        // debug('consent', 'GA4 configured - page_view will fire automatically')
      }
    }

    // Analytics category: GA4 + Clarity (session recording)
    if (analyticsAccepted) {
      // Load Microsoft Clarity if enabled (from globals.json tracking.clarity config)
      const clarityConfig = this._config?.clarity
      if (clarityConfig?.enabled && clarityConfig?.project_id && typeof window.clarity === 'undefined') {
        // debug('consent', 'Loading Microsoft Clarity')
        (function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)}
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y)
        })(window, document, "clarity", "script", clarityConfig.project_id)
        window.clarity("consent")
        // debug('consent', 'Microsoft Clarity initialized')

        // Identify user for session recording (uses current_user from globals.json)
        if (window.current_user) {
          const user = window.current_user
          window.clarity("identify", String(user.id), user.visit_id ? String(user.visit_id) : null, null, String(user.id))
          window.clarity('set', 'visitId', user.visit_id ? String(user.visit_id) : null)
          window.clarity('set', 'isEmp', String(user.isEmp))
          window.clarity('set', 'isGuest', String(user.isGuest))
          window.clarity('set', 'isWyIp', String(user.isWyIp))
          window.clarity('set', 'isUser', String(user.isUser))
          window.clarity('set', 'environment', window.env)
          window.clarity('set', 'source', user.src)
          window.clarity('set', 'cartId', user.cartId)
        }
      }
    }

    // Marketing category: Facebook Pixel + Bing UET
    if (marketingAccepted) {
      // debug('consent', 'Loading marketing pixels (marketing accepted)')
      
      // Load Facebook Pixel
      if (!window.fbq) {
        // debug('consent', 'Loading Facebook Pixel')
        !function(f,b,e,v,n,t,s) {
          if (f.fbq) return
          n = f.fbq = function() { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments) }
          if (!f._fbq) f._fbq = n
          n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = []
          t = b.createElement(e); t.async = !0; t.src = v
          s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s)
        }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
        window.fbq('init', '261770050896557')
        window.fbq('track', 'PageView')
        // debug('consent', 'Facebook Pixel initialized - PageView fired')
      }

      // Load Bing UET
      if (!window.uetq) {
        // debug('consent', 'Loading Bing UET');
        (function(w, d, t, r, u) {
          var f, n, i
          w[u] = w[u] || []
          f = function() {
            var o = { ti: '259003029', enableAutoSpaTracking: true }
            o.q = w[u]; w[u] = new UET(o); w[u].push('pageLoad')
          }
          n = d.createElement(t); n.src = r; n.async = 1
          n.onload = n.onreadystatechange = function() {
            var s = this.readyState
            if (!s || s === 'loaded' || s === 'complete') { f(); n.onload = n.onreadystatechange = null }
          }
          i = d.getElementsByTagName(t)[0]; i.parentNode.insertBefore(n, i)
        })(window, document, 'script', '//bat.bing.com/bat.js', 'uetq')
      }
    }

    // MaxMind device.js - fraud detection (analytics category, not consent-gated)
    // Used for minFraud service to detect device fingerprint
    if (analyticsAccepted && !window.__mmapiws) {
      window.__mmapiws = { accountId: '97659' }
      const deviceScript = document.createElement('script')
      deviceScript.async = true
      deviceScript.src = 'https://device.maxmind.com/js/device.js'
      document.body.appendChild(deviceScript)
    }
  }

  // Public API methods
  static showPreferences() {
    // Initialize CookieConsent UI on-demand if not already done (for opt-out regions)
    if (!this._cookieConsentInitialized) {
      const config = this._config
      const isOptOut = config?.consent_mode === CONSENT_MODE.OPT_OUT
      const isImplied = config?.consent_mode === CONSENT_MODE.IMPLIED

      // For opt-out/implied regions, cookie should already exist from _enableTrackingWithoutUI
      // But if it doesn't, set it now before initializing
      if (isOptOut || isImplied) {
        const existingCookie = this._getCookieValue('cc_cookie')

        if (!existingCookie) {
          // No cookie exists - set it based on server config
          const consent = config?.consent || {}
          const analyticsEnabled = consent.analytics !== false
          const marketingEnabled = consent.marketing !== false && !config?.has_gpc

          const categories = [CAT_NECESSARY]
          if (analyticsEnabled) categories.push(CAT_ANALYTICS)
          if (marketingEnabled) categories.push(CAT_MARKETING)

          // Pre-set cookie - use same format as _enableTrackingWithoutUI
          const expires = new Date()
          expires.setFullYear(expires.getFullYear() + 1)
          const cookieValue = JSON.stringify({
            categories: categories,
            revision: 0,
            consentTimestamp: consent.updated_at || new Date().toISOString()
          })
          document.cookie = `cc_cookie=${encodeURIComponent(cookieValue)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`
        }
      }

      // Initialize CookieConsent
      // autoShow is set to false in _initCookieConsentUI() for opt-out/implied with existing cookie
      this._initCookieConsentUI()

      // Small delay to ensure CookieConsent.run() completes initialization
      // Then show preferences modal
      setTimeout(() => {
        CookieConsent.showPreferences()
      }, 10)
      return
    }

    CookieConsent.showPreferences()
  }

  /**
   * Helper to get cookie value
   */
  static _getCookieValue(name) {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) {
      return parts.pop().split(';').shift()
    }
    return null
  }
  static show() { CookieConsent.show(true) }
  static acceptAll() { CookieConsent.acceptCategory('all') }
  static rejectAll() { CookieConsent.acceptCategory([]) }
  static acceptedCategory(category) {
    if (category === CAT_MARKETING && (this._config?.has_gpc || navigator.globalPrivacyControl === true)) {
      return false
    }
    // Use server config if CookieConsent not yet initialized
    if (!this._cookieConsentInitialized) {
      if (category === CAT_ANALYTICS) return this._config?.consent?.analytics !== false
      if (category === CAT_MARKETING) return this._config?.consent?.marketing !== false
      return true // necessary always accepted
    }
    return CookieConsent.acceptedCategory(category)
  }
  static getConsentMode() { return this._config?.consent_mode }
  static enableDebug() {
    this._debug = true
    console.log('[ConsentManager] Debug enabled, config:', this._config)
  }
}

// Expose for global access
window.ConsentManager = ConsentManager

export default ConsentManager
